<template>
  <div class="content">
    <div class="ai-builder-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-ai-builder">

          <header class="glad-ai-builder__head">
            <p class="glad-eyebrow">NTC Admin · AI tools</p>
            <h1 class="glad-ai-builder__title">AI Course Builder</h1>
            <p class="glad-ai-builder__sub">
              Draft a Project GLAD&reg;-aligned course outline from a topic. Review and commit
              to your pathway library as a draft course — edit in the regular course tools.
            </p>
          </header>

          <!-- INPUT FORM -->
          <section v-if="!outline" class="glad-form">
            <div class="glad-form__row">
              <label class="glad-form__label" for="ai-topic">Topic *</label>
              <input
                id="ai-topic"
                v-model="form.topic"
                type="text"
                maxlength="300"
                placeholder="e.g. Pictorial Input Charts for elementary science"
                class="glad-form__input" />
            </div>
            <div class="glad-form__grid">
              <div class="glad-form__col">
                <label class="glad-form__label" for="ai-grade">Audience</label>
                <select id="ai-grade" v-model="form.grade_band" class="glad-form__input">
                  <option value="K-2">K-2</option>
                  <option value="3-5">3-5</option>
                  <option value="6-8">6-8</option>
                  <option value="9-12">9-12</option>
                  <option value="adult">Adult / Professional</option>
                </select>
              </div>
              <div class="glad-form__col">
                <label class="glad-form__label" for="ai-duration">Total duration (min)</label>
                <input
                  id="ai-duration"
                  v-model.number="form.duration_min"
                  type="number"
                  min="15"
                  max="600"
                  step="5"
                  class="glad-form__input" />
              </div>
            </div>

            <div class="glad-form__row">
              <label class="glad-form__label" for="ai-strategies">Anchor strategies (optional)</label>
              <input
                id="ai-strategies"
                v-model="form.focus_strategies_raw"
                type="text"
                placeholder="comma-separated, e.g. CCD, Pictorial Input Chart, Process Grid"
                class="glad-form__input" />
              <p class="glad-form__hint">
                Leave blank to let the model choose the best-fit GLAD&reg; strategies for the topic.
              </p>
            </div>

            <div class="glad-form__actions">
              <button
                type="button"
                class="glad-btn"
                :disabled="generating || !canGenerate"
                @click="generate">
                {{ generating ? 'Drafting…' : 'Draft outline' }}
              </button>
            </div>

            <p v-if="error" class="glad-form__error" role="alert">{{ error }}</p>
          </section>

          <!-- OUTLINE PREVIEW -->
          <section v-else class="glad-outline">
            <header class="glad-outline__head">
              <div>
                <p class="glad-eyebrow">Draft outline</p>
                <input v-model="outline.title" class="glad-outline__title" />
                <textarea v-model="outline.description" rows="3" class="glad-outline__description"></textarea>
              </div>
              <div class="glad-outline__meta">
                <span>{{ outline.meta.grade_band }}</span>
                <span>·</span>
                <span>{{ totalDuration }} min total</span>
                <span>·</span>
                <span>{{ outline.lessons.length }} lessons</span>
              </div>
            </header>

            <ol class="glad-lessons">
              <li
                v-for="(lesson, idx) in outline.lessons"
                :key="idx"
                class="glad-lesson">
                <div class="glad-lesson__num">{{ idx + 1 }}</div>
                <div class="glad-lesson__body">
                  <div class="glad-lesson__row">
                    <input
                      v-model="lesson.title"
                      class="glad-lesson__title"
                      :aria-label="'Lesson ' + (idx + 1) + ' title'" />
                    <input
                      v-model.number="lesson.duration_min"
                      type="number"
                      min="5"
                      max="180"
                      class="glad-lesson__duration"
                      :aria-label="'Lesson ' + (idx + 1) + ' duration in minutes'" />
                  </div>
                  <textarea
                    v-model="lesson.summary"
                    rows="3"
                    class="glad-lesson__summary"
                    :aria-label="'Lesson ' + (idx + 1) + ' summary'"></textarea>
                  <div class="glad-lesson__strategy">
                    <span class="glad-lesson__pill" v-if="lesson.glad_strategy">{{ lesson.glad_strategy }}</span>
                    <span class="glad-lesson__hint" v-if="lesson.resources_hint">{{ lesson.resources_hint }}</span>
                  </div>
                </div>
                <button
                  type="button"
                  class="glad-lesson__remove"
                  aria-label="Remove lesson"
                  @click="outline.lessons.splice(idx, 1)">×</button>
              </li>
            </ol>

            <div class="glad-outline__actions">
              <button type="button" class="glad-btn glad-btn--ghost" @click="discard">Discard</button>
              <button type="button" class="glad-btn glad-btn--ghost" @click="generate">Regenerate</button>
              <button type="button" class="glad-btn" :disabled="committing || !canCommit" @click="commit">
                {{ committing ? 'Saving…' : 'Save as draft course' }}
              </button>
            </div>

            <p v-if="committed" class="glad-form__notice">
              Saved as draft course <strong>#{{ committed.course_id }}</strong>.
              <router-link
                v-if="committed.course_id"
                :to="'/glad/courses/' + committed.course_id + '/edit'">Open in editor →</router-link>
            </p>

            <p v-if="error" class="glad-form__error" role="alert">{{ error }}</p>
          </section>

        </div>
      </card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AiCourseBuilder',
  data() {
    return {
      form: {
        topic: '',
        grade_band: 'adult',
        duration_min: 90,
        focus_strategies_raw: '',
      },
      generating: false,
      committing: false,
      outline: null,
      committed: null,
      error: '',
    };
  },
  computed: {
    canGenerate() {
      return this.form.topic && this.form.topic.trim().length > 4;
    },
    canCommit() {
      return this.outline
        && this.outline.title.trim()
        && this.outline.lessons.length > 0
        && this.outline.lessons.every(l => l.title.trim());
    },
    totalDuration() {
      if (!this.outline) return 0;
      return this.outline.lessons.reduce((s, l) => s + (Number(l.duration_min) || 0), 0);
    },
  },
  methods: {
    generate() {
      this.error = '';
      this.committed = null;
      this.generating = true;
      const focus = (this.form.focus_strategies_raw || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      this.$http.post('glad/ai/course-outline', {
        topic:            this.form.topic.trim(),
        grade_band:       this.form.grade_band,
        duration_min:     this.form.duration_min,
        focus_strategies: focus,
        gladRole:         (typeof localStorage !== 'undefined') ? (localStorage.getItem('hot-user') || '') : '',
      })
        .then(resp => { this.outline = resp.data.outline; })
        .catch(err => { this.error = this.errorMessage(err) || 'Could not draft the outline.'; })
        .finally(() => { this.generating = false; });
    },
    commit() {
      this.committing = true;
      this.error = '';
      this.$http.post('glad/ai/course-outline/commit', {
        outline: this.outline,
        gladRole: (typeof localStorage !== 'undefined') ? (localStorage.getItem('hot-user') || '') : '',
      })
        .then(resp => { this.committed = resp.data; })
        .catch(err => { this.error = this.errorMessage(err) || 'Could not save the course.'; })
        .finally(() => { this.committing = false; });
    },
    discard() {
      if (!window.confirm('Discard this draft outline?')) return;
      this.outline = null;
      this.committed = null;
    },
    errorMessage(err) {
      const data = (err && err.response && err.response.data) || {};
      const msg = data.message || '';
      const detail = data.detail || '';
      // Surface a friendlier message for the common rate-limit case so the
      // user knows it's not a code bug; otherwise pass through.
      if (/HTTP 429/i.test(detail) || /quota/i.test(detail)) {
        return 'AI quota for the day has been reached. Try again in a few minutes (free-tier limit). Underlying error: ' + detail.slice(0, 200);
      }
      if (msg && detail) return msg + ' (' + detail.slice(0, 200) + ')';
      return msg || '';
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.ai-builder-wrap { width: 100%; }
.ai-builder-wrap ::v-deep > .card.no-border-card { border: 0; box-shadow: none; }
.glad-ai-builder {
  padding: 1.5rem 1.5rem 2.5rem;
  font-family: var(--glad-font-sans);
}

.glad-ai-builder__head { margin-bottom: 1.75rem; }
.glad-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--glad-accent);
  display: block;
  margin: 0 0 0.4rem;
}
.glad-ai-builder__title {
  font-family: var(--glad-font-serif);
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.35rem;
  line-height: 1.2;
}
.glad-ai-builder__sub {
  color: var(--glad-text-soft);
  font-size: 0.9rem;
  margin: 0;
  max-width: 660px;
  line-height: 1.55;
}

/* Form -------------------------------------------------------------------- */
.glad-form { max-width: 720px; }
.glad-form__row { margin-bottom: 1.1rem; }
.glad-form__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
  margin-bottom: 1.1rem;
}
.glad-form__label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--glad-text-soft);
  margin-bottom: 0.35rem;
}
.glad-form__input {
  display: block;
  width: 100%;
  padding: 0.55rem 0.75rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius);
  font-family: var(--glad-font-sans);
  font-size: 0.9rem;
  color: var(--glad-ink);
  &:focus { outline: 0; border-color: var(--glad-ink); }
}
.glad-form__hint {
  font-size: 0.74rem;
  color: var(--glad-text-muted);
  margin: 0.35rem 0 0;
}
.glad-form__actions { display: flex; gap: 0.6rem; }
.glad-form__error {
  margin-top: 0.85rem;
  font-size: 0.84rem;
  color: var(--glad-danger);
}
.glad-form__notice {
  margin-top: 1rem;
  padding: 0.6rem 0.85rem;
  background: rgba(21, 128, 61, 0.08);
  border-radius: var(--glad-radius);
  color: #15803d;
  font-size: 0.85rem;
  a { color: #15803d; font-weight: 600; }
}

/* Outline ------------------------------------------------------------------ */
.glad-outline { max-width: 820px; }

.glad-outline__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--glad-line);
}
.glad-outline__title {
  display: block;
  width: 100%;
  font-family: var(--glad-font-serif);
  font-size: 1.45rem;
  font-weight: 700;
  color: var(--glad-ink);
  background: transparent;
  border: 0;
  outline: 0;
  padding: 0;
  margin: 0 0 0.4rem;
  &:focus { background: var(--glad-paper); padding: 0.15rem 0.35rem; border-radius: 4px; }
}
.glad-outline__description {
  display: block;
  width: 100%;
  font-family: var(--glad-font-sans);
  font-size: 0.9rem;
  color: var(--glad-text-soft);
  line-height: 1.55;
  background: transparent;
  border: 0;
  outline: 0;
  padding: 0;
  resize: vertical;
  &:focus { background: var(--glad-paper); padding: 0.25rem 0.35rem; border-radius: 4px; }
}
.glad-outline__meta {
  flex-shrink: 0;
  font-size: 0.78rem;
  color: var(--glad-text-muted);
  display: flex;
  gap: 0.35rem;
  align-items: center;
}

/* Lessons ----------------------------------------------------------------- */
.glad-lessons { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.85rem; }
.glad-lesson {
  display: flex;
  gap: 0.85rem;
  padding: 1rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
  position: relative;
}
.glad-lesson__num {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--glad-ink);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.glad-lesson__body { flex: 1; min-width: 0; }
.glad-lesson__row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.glad-lesson__title {
  flex: 1;
  font-family: var(--glad-font-serif);
  font-size: 1rem;
  font-weight: 600;
  color: var(--glad-ink);
  background: transparent;
  border: 0;
  outline: 0;
  padding: 0;
  &:focus { background: var(--glad-paper); padding: 0.15rem 0.35rem; border-radius: 4px; }
}
.glad-lesson__duration {
  width: 70px;
  text-align: right;
  border: 1px solid var(--glad-line);
  border-radius: 6px;
  padding: 0.25rem 0.4rem;
  font-family: var(--glad-font-sans);
  font-size: 0.8rem;
  background: var(--glad-paper);
  &::-webkit-inner-spin-button { -webkit-appearance: none; }
}
.glad-lesson__summary {
  display: block;
  width: 100%;
  font-family: var(--glad-font-sans);
  font-size: 0.85rem;
  color: var(--glad-text-soft);
  line-height: 1.5;
  background: var(--glad-paper);
  border: 1px solid var(--glad-line);
  border-radius: 6px;
  padding: 0.45rem 0.6rem;
  resize: vertical;
  &:focus { outline: 0; border-color: var(--glad-ink); }
}
.glad-lesson__strategy {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-top: 0.5rem;
}
.glad-lesson__pill {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.18rem 0.5rem;
  background: rgba(180, 83, 9, 0.10);
  color: var(--glad-accent);
  border-radius: 999px;
}
.glad-lesson__hint {
  font-size: 0.74rem;
  color: var(--glad-text-muted);
  font-style: italic;
}
.glad-lesson__remove {
  position: absolute;
  top: 0.35rem;
  right: 0.5rem;
  background: transparent;
  border: 0;
  color: var(--glad-text-muted);
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.2rem 0.45rem;
  &:hover { color: var(--glad-danger); }
}

.glad-outline__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.5rem;
}

.glad-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.1rem;
  background: var(--glad-ink);
  color: #fff;
  border: 0;
  border-radius: var(--glad-radius);
  font-family: var(--glad-font-sans);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &--ghost {
    background: transparent;
    color: var(--glad-text-soft);
    border: 1.5px solid var(--glad-line);
    &:hover { background: var(--glad-paper-2); opacity: 1; }
  }
}
</style>
